import { merge, set } from 'lodash';
import { useCallback } from 'react';
import { Resolver } from 'react-hook-form';
import { ObjectSchema, Shape, ValidationError } from 'yup';

export const useYupValidationResolver = <
  FormValues extends object,
  ValidationContext extends object = object,
>(
  validationSchema: ObjectSchema<Shape<object | undefined, FormValues>>,
  fieldArrayPathFormat?: 'yup' | 'react-hook-form',
) =>
  useCallback(
    async (data) => {
      try {
        const values = await validationSchema.validate(data, {
          abortEarly: false,
        });

        return {
          values,
          errors: {},
        };
      } catch (errors) {
        return {
          values: {},
          errors: (errors as ValidationError).inner.reduce(
            (allErrors, currentError) => {
              /** TODO Migrate to 'react-hook-form' format
               * Check existing forms with useFieldArray for
               * any relevant workarounds for displaying errors
               * with 'yup' style error paths, and update them
               * before making 'react-hook-form' format
               * the default path format on errors.
               * Currently defaults to 'yup' format.
               */
              switch (fieldArrayPathFormat) {
                case 'react-hook-form':
                  return merge(
                    allErrors,
                    set({}, currentError.path, {
                      type: currentError.type ?? 'validation',
                      message: currentError.message,
                    }),
                  );
                case 'yup':
                default:
                  return {
                    ...allErrors,
                    [currentError.path]: {
                      type: currentError.type ?? 'validation',
                      message: currentError.message,
                    },
                  };
              }
            },
            {},
          ),
        };
      }
    },
    [validationSchema, fieldArrayPathFormat],
  ) as Resolver<FormValues, ValidationContext>;
